import { useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, Phone, Mail, Users, Cake, Building2, Heart, Send, Loader2 } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/Reveal";
import { SITE } from "@/data/site";

const EASE = [0.22, 1, 0.36, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OCCASIONS = [
  { icon: Building2, label: "Office lunches" },
  { icon: Cake, label: "Birthdays" },
  { icon: Heart, label: "Weddings" },
  { icon: Users, label: "Community events" },
];

const inputCls =
  "w-full rounded-lg border border-sand bg-cream px-4 py-3 text-sm text-ink outline-none transition-colors duration-300 placeholder:text-smoke/50 focus:border-rust focus:ring-2 focus:ring-rust/20";

const Field = ({ label, htmlFor, children }) => (
  <div>
    <label htmlFor={htmlFor} className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-smoke">
      {label}
    </label>
    {children}
  </div>
);

export default function Catering() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", event_date: "", guests: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");
    try {
      await axios.post(`${API}/catering`, {
        name: form.name,
        phone: form.phone,
        email: form.email,
        event_date: form.event_date || null,
        guests: form.guests ? Number(form.guests) : null,
        message: form.message,
      });
      setSent(true);
    } catch (err) {
      setError("Something went wrong sending your enquiry. Please try again, or call us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="catering-page">
      <section className="relative overflow-hidden">
        <span
          aria-hidden="true"
          className="outline-word-dark pointer-events-none absolute -right-8 top-4 select-none font-display text-[18vw] font-black leading-none"
        >
          CATER
        </span>
        <div className="relative mx-auto max-w-7xl px-5 pb-10 pt-16 sm:px-8 lg:pt-24">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="font-mono text-xs uppercase tracking-[0.3em] text-rust"
          >
            Catering Enquiries
          </motion.p>
          <MaskedLines
            className="mt-6 font-display text-4xl font-black leading-[1.05] tracking-tight text-ink sm:text-6xl lg:text-7xl"
            lines={[{ text: "Feeding a crowd?" }, { text: "Sorted.", cls: "italic text-rust" }]}
          />
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8, ease: EASE }}
            className="mt-6 max-w-xl text-base leading-relaxed text-smoke"
          >
            Tell us about your event below and Ray will get back to you within 24 hours with options and pricing.
            Halal catering available.
          </motion.p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-24 sm:px-8">
        <div className="grid gap-10 lg:grid-cols-5">
          <Reveal className="lg:col-span-2">
            <div className="flex h-full flex-col gap-8 overflow-hidden rounded-2xl border border-sand bg-espresso text-cream">
              <img
                src="/shop/jacket.jpg"
                alt="Ray at the entrance of Babylon Deli"
                className="aspect-[16/10] w-full object-cover object-top"
              />
              <div className="px-8">
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-amber">What we cater</p>
                <ul className="mt-5 space-y-4">
                  {OCCASIONS.map((o) => (
                    <li key={o.label} className="flex items-center gap-4">
                      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-cream/10 text-amber">
                        <o.icon className="h-4 w-4" />
                      </span>
                      <span className="font-display text-lg font-bold">{o.label}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-auto border-t border-cream/15 px-8 pb-8 pt-6">
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-amber">Rather talk?</p>
                <a
                  href={SITE.phoneHref}
                  data-testid="catering-phone-link"
                  className="mt-3 flex items-center gap-3 text-sm text-cream/85 transition-colors duration-300 hover:text-amber"
                >
                  <Phone className="h-4 w-4 text-amber" />
                  {SITE.phone} · {SITE.contact}
                </a>
                <a
                  href={SITE.emailHref}
                  data-testid="catering-email-link"
                  className="mt-3 flex items-center gap-3 text-sm text-cream/85 transition-colors duration-300 hover:text-amber"
                >
                  <Mail className="h-4 w-4 text-amber" />
                  {SITE.email}
                </a>
              </div>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-3" delay={0.12}>
            <div className="rounded-2xl border border-sand bg-card p-7 shadow-sm sm:p-9">
              <AnimatePresence mode="wait">
                {sent ? (
                  <motion.div
                    key="success"
                    data-testid="catering-success-panel"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: EASE }}
                    className="flex flex-col items-start gap-5 py-10"
                  >
                    <span className="grid h-14 w-14 place-items-center rounded-full bg-moss text-cream">
                      <CheckCircle2 className="h-7 w-7" />
                    </span>
                    <h3 className="font-display text-3xl font-bold text-ink">Thanks, {form.name.split(" ")[0]}!</h3>
                    <p className="max-w-md text-sm leading-relaxed text-smoke">
                      Your catering enquiry is in. Ray will get back to you within 24 hours — usually much sooner.
                      Need us urgently? Call {SITE.phone}.
                    </p>
                    <button
                      data-testid="catering-another-button"
                      onClick={() => {
                        setSent(false);
                        setForm({ name: "", phone: "", email: "", event_date: "", guests: "", message: "" });
                      }}
                      className="rounded-full border border-ink/25 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.18em] text-ink transition-colors duration-300 hover:border-rust hover:text-rust"
                    >
                      Send another enquiry
                    </button>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    data-testid="catering-form"
                    onSubmit={submit}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="grid gap-5 sm:grid-cols-2"
                  >
                    <Field label="Your name *" htmlFor="catering-name">
                      <input
                        id="catering-name"
                        data-testid="catering-name-input"
                        required
                        value={form.name}
                        onChange={set("name")}
                        placeholder="Jane Citizen"
                        className={inputCls}
                      />
                    </Field>
                    <Field label="Phone *" htmlFor="catering-phone">
                      <input
                        id="catering-phone"
                        data-testid="catering-phone-input"
                        required
                        type="tel"
                        value={form.phone}
                        onChange={set("phone")}
                        placeholder="04XX XXX XXX"
                        className={inputCls}
                      />
                    </Field>
                    <Field label="Email *" htmlFor="catering-email">
                      <input
                        id="catering-email"
                        data-testid="catering-email-input"
                        required
                        type="email"
                        value={form.email}
                        onChange={set("email")}
                        placeholder="you@example.com"
                        className={inputCls}
                      />
                    </Field>
                    <Field label="Event date" htmlFor="catering-date">
                      <input
                        id="catering-date"
                        data-testid="catering-date-input"
                        type="date"
                        value={form.event_date}
                        onChange={set("event_date")}
                        className={inputCls}
                      />
                    </Field>
                    <Field label="Number of guests" htmlFor="catering-guests">
                      <input
                        id="catering-guests"
                        data-testid="catering-guests-input"
                        type="number"
                        min="1"
                        value={form.guests}
                        onChange={set("guests")}
                        placeholder="e.g. 30"
                        className={inputCls}
                      />
                    </Field>
                    <div className="sm:col-span-2">
                      <Field label="Tell us about your event *" htmlFor="catering-message">
                        <textarea
                          id="catering-message"
                          data-testid="catering-message-input"
                          required
                          rows={5}
                          value={form.message}
                          onChange={set("message")}
                          placeholder="What are you celebrating, roughly how many people, and any sandwiches or dietary needs in mind?"
                          className={`${inputCls} resize-none`}
                        />
                      </Field>
                    </div>

                    {error && (
                      <p data-testid="catering-error" className="text-sm font-semibold text-rust sm:col-span-2">
                        {error}
                      </p>
                    )}

                    <div className="sm:col-span-2">
                      <button
                        type="submit"
                        data-testid="catering-submit-button"
                        disabled={sending}
                        className="group inline-flex w-full items-center justify-center gap-3 rounded-full bg-moss px-7 py-4 font-mono text-xs uppercase tracking-[0.18em] text-cream transition-colors duration-300 hover:bg-moss-dark disabled:opacity-60 sm:w-auto"
                      >
                        {sending ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Sending…
                          </>
                        ) : (
                          <>
                            Send Enquiry
                            <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                          </>
                        )}
                      </button>
                    </div>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
